import { Employment, Unemployment } from '../models/employment.model';

export const EMPLOYMENT_STATE_EMPLOYED = 'employed';
export const EMPLOYMENT_STATE_UNEMPLOYED = 'unemployed';

export type EmploymentRecordState = typeof EMPLOYMENT_STATE_EMPLOYED | typeof EMPLOYMENT_STATE_UNEMPLOYED;

/** Shown as a non-blocking warning when employed/unemployed periods overlap opposite-type records. */
export const CROSS_STATE_OVERLAP_WARNING_MESSAGE =
  'This period overlaps with an existing employed/unemployed record. You can still save, but please confirm the dates are correct.';

function rangeEndMs(item: Employment | Unemployment, currentMonthEnd: Date): number {
  if (item?.toDate != null && String(item.toDate).trim() !== '') {
    return new Date(item.toDate).getTime();
  }
  return new Date(currentMonthEnd).getTime();
}

/**
 * True if `candidate` overlaps any list item of the opposite employment state.
 * Skips rows whose `id` is in `excludeRecordIds`.
 */
export function hasOppositeStateDateOverlap(
  candidate: Employment | Unemployment,
  candidateState: EmploymentRecordState,
  list: (Employment | Unemployment)[],
  currentMonthEnd: Date,
  excludeRecordIds?: string[]
): boolean {
  if (!candidate?.fromDate) {
    return false;
  }

  const exclude = new Set((excludeRecordIds || []).filter(id => !!id));
  const cStart = new Date(candidate.fromDate).getTime();
  const cEnd = rangeEndMs(candidate, currentMonthEnd);

  for (const item of list) {
    if (!item?.fromDate) {
      continue;
    }
    if (item.id && exclude.has(item.id)) {
      continue;
    }

    const itemState = getRecordEmploymentState(item);
    if (itemState === candidateState) {
      continue;
    }

    const iStart = new Date(item.fromDate).getTime();
    const iEnd = rangeEndMs(item, currentMonthEnd);

    if (cStart <= iEnd && iStart <= cEnd) {
      return true;
    }
  }

  return false;
}

export function getRecordEmploymentState(item: Employment | Unemployment): EmploymentRecordState {
  const raw = item?.employmentState?.toLowerCase();
  if (raw === EMPLOYMENT_STATE_EMPLOYED || raw === EMPLOYMENT_STATE_UNEMPLOYED) {
    return raw;
  }
  const withEmployer = item as Employment;
  if (withEmployer?.employer != null && String(withEmployer.employer).trim() !== '') {
    return EMPLOYMENT_STATE_EMPLOYED;
  }
  return EMPLOYMENT_STATE_UNEMPLOYED;
}
